import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Validate stored token on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('admin_token');
      const storedUser = localStorage.getItem('admin_user');

      if (storedToken && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setToken(storedToken);
          setUser(parsedUser);

          // Optionally verify token with server (don't auto-logout on failure)
          try {
            const res = await api.get('/admin/me');
            if (res.data && res.data.user) {
              setUser(res.data.user);
              localStorage.setItem('admin_user', JSON.stringify(res.data.user));
            }
          } catch (verifyErr) {
            // On 401: token is invalid — clear and force re-login
            if (verifyErr.response?.status === 401) {
              localStorage.removeItem('admin_token');
              localStorage.removeItem('admin_user');
              setToken(null);
              setUser(null);
            }
            // For network errors or other issues: keep stored user (offline-tolerant)
          }
        } catch (e) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/admin/v2/login', { email, password });
    const newToken = res.data.token || res.data.access_token;
    const newUser = res.data.user;

    localStorage.setItem('admin_token', newToken);
    localStorage.setItem('admin_user', JSON.stringify(newUser));
    if (res.data.refresh_token) {
      localStorage.setItem('admin_refresh_token', res.data.refresh_token);
    }

    setToken(newToken);
    setUser(newUser);

    return newUser;
  }, []);

  const register = useCallback(async (storeName, email, password) => {
    const res = await api.post('/admin/v2/register', { name: storeName, email, password });
    const newToken = res.data.token || res.data.access_token;
    const newUser = res.data.user;

    localStorage.setItem('admin_token', newToken);
    localStorage.setItem('admin_user', JSON.stringify(newUser));

    setToken(newToken);
    setUser(newUser);

    return newUser;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setToken(null);
    setUser(null);
    window.location.href = '/login';
  }, []);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('admin_user', JSON.stringify(updatedUser));
  }, []);

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated: !!token && !!user,
    login,
    register,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
